'use client';

import React, { useEffect, useState } from 'react';
import { AutoGrowTextarea } from '@/components/picture-book-flow/AutoGrowTextarea';
import { DEDICATION_MAX_CHARS } from '@/lib/books/dedication';
import { trackCreate } from '@/utils/create-flow-analytics';
import { bookRequestHeaders } from './request-headers';
import {
  COMIC_DEDICATION_HELP,
  COMIC_DEDICATION_LOCKED_MESSAGE,
  COMIC_DEDICATION_PLACEHOLDER,
  dedicationClientError,
  dedicationSaveNeeded,
  type DedicationPutResult,
  putComicDedication,
  shouldTrackDedicationSaved,
} from './comic-dedication';

/** Dedication field on the comic preview: shown at once, saved on blur, read-only once the book is ordered. */
export function ComicDedicationField({
  bookId,
  pendingSave,
}: {
  bookId: string;
  /** MangaGenerateStep awaits this before checkout. */
  pendingSave: React.MutableRefObject<Promise<DedicationPutResult | null>>;
}) {
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState('');
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/books/${encodeURIComponent(bookId)}/comic-dedication`, {
          headers: await bookRequestHeaders(),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const d = typeof data.dedication === 'string' ? data.dedication : '';
        // Do not clobber text the parent has already started typing.
        setValue((current) => (current === '' ? d : current));
        setSaved(d);
        if (data.editable === false) setLocked(true);
      } catch {
        // The field still works; the PUT reports any real problem.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookId]);

  const clientError = dedicationClientError(value);

  const save = () => {
    if (locked || !dedicationSaveNeeded(value, saved)) return;
    const text = value;
    const prev = saved;
    const run = (async (): Promise<DedicationPutResult> => {
      const bad = dedicationClientError(text);
      if (bad) {
        setError(bad);
        return { kind: 'error', message: bad };
      }
      const r = await putComicDedication(bookId, text);
      if (r.kind === 'locked') {
        setLocked(true);
        setError(null);
      } else if (r.kind === 'error') {
        setError(r.message);
      } else {
        setError(null);
        setSaved(r.dedication);
        setValue(r.dedication); // show what the server stored (stripped characters disappear)
        if (shouldTrackDedicationSaved(prev, r.dedication)) trackCreate('comic', 'dedication_saved');
      }
      return r;
    })();
    pendingSave.current = run;
  };

  return (
    <div className="text-left" id="comic-dedication-field">
      <label className="block text-sm font-semibold text-gray-700" htmlFor="comic-dedication">
        Dedication (optional)
      </label>
      <AutoGrowTextarea
        id="comic-dedication"
        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 bg-white"
        rows={3}
        maxLength={DEDICATION_MAX_CHARS}
        placeholder={COMIC_DEDICATION_PLACEHOLDER}
        value={value}
        readOnly={locked}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
      />
      <p className="text-xs text-gray-500 mt-1">
        {locked ? COMIC_DEDICATION_LOCKED_MESSAGE : COMIC_DEDICATION_HELP} {Array.from(value).length}/{DEDICATION_MAX_CHARS}
      </p>
      {(clientError || error) && (
        <p className="text-sm text-red-600 mt-1" role="alert">{clientError || error}</p>
      )}
    </div>
  );
}
